/**
 * Date-only helpers for the UI (spec 2: dates are ISO strings everywhere).
 * Pure TypeScript mirrors of the engine's dates.js so the main bundle does
 * not need the engine; a test pins them to the engine's results.
 */

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDate(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const m = ISO_RE.exec(s);
  if (!m) return false;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

function parts(iso: string): { y: number; m: number; d: number } {
  const m = ISO_RE.exec(iso);
  if (!m) throw new Error('Invalid ISO date: ' + iso);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function toIso(y: number, m: number, d: number): string {
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** 0 = Sunday, matching the engine and aircraft_types.swap_weekday. */
export function weekdayIndex(iso: string): number {
  const { y, m, d } = parts(iso);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function weekdayName(dowOrIso: number | string): string {
  const dow = typeof dowOrIso === 'number' ? dowOrIso : weekdayIndex(dowOrIso);
  return WEEKDAYS[dow] ?? '';
}

/** Month arithmetic with day-of-month clamping (D5): Aug 31 + 6 months = Feb 28/29. */
export function addMonthsIso(iso: string, months: number): string {
  const { y, m, d } = parts(iso);
  const first = new Date(Date.UTC(y, m - 1 + months, 1));
  const ty = first.getUTCFullYear();
  const tm = first.getUTCMonth();
  const daysInTarget = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  return toIso(ty, tm + 1, Math.min(d, daysInTarget));
}

/** First date on or after `iso` that falls on weekday `dow` (0 = Sunday); mirrors the engine's firstOnOrAfter. */
export function firstWeekdayOnOrAfter(iso: string, dow: number): string {
  const delta = (dow - weekdayIndex(iso) + 7) % 7;
  return addDaysIso(iso, delta);
}

export function addDaysIso(iso: string, days: number): string {
  const { y, m, d } = parts(iso);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return toIso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** Whole days since 1970-01-01, matching the engine's epoch days. */
export function epochDay(iso: string): number {
  const { y, m, d } = parts(iso);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
}

export function isoFromEpochDay(day: number): string {
  const dt = new Date(day * 86_400_000);
  return toIso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** Whole days from a to b (b - a). */
export function daysBetween(a: string, b: string): number {
  const pa = parts(a), pb = parts(b);
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / 86_400_000);
}

export function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** "Wed 05 Aug 2026" (weekday beside every date that matters, spec 5.13). */
export function fmtDate(iso: string | null | undefined, opts: { weekday?: boolean; year?: boolean } = {}): string {
  if (!iso) return '';
  if (!isIsoDate(iso)) return iso;
  const { y, m, d } = parts(iso);
  const wd = opts.weekday === false ? '' : weekdayName(iso).slice(0, 3) + ' ';
  const yr = opts.year === false ? '' : ' ' + y;
  return `${wd}${pad(d)} ${MONTHS[m - 1]}${yr}`;
}

export function fmtDateTime(ts: string | null | undefined): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function pilotLabel(p: { last_name: string; first_name: string; code: string }): string {
  return `${p.last_name}, ${p.first_name} (${p.code})`;
}
